// src/riskFlow/RiskActionPlan.js
// Enhanced action plan component with slide-in saved actions and save animation

import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card, Text, Button, Stack, Group, Container, Badge, 
  ActionIcon, Alert, Grid, Title, Transition,
  Divider, Paper
} from '@mantine/core';
import {
  IconDeviceFloppy, IconTrash, IconEdit, 
  IconCheck, IconX, IconAlertCircle,
  IconSparkles
} from '@tabler/icons-react';
import AIGeneratingIcon from '../components/AIGeneratingIcon';
import { notifications } from '@mantine/notifications';


import {
  saveControl, discardControl, discardSavedControl,
  editGeneratedActionPlanRow, fetchControls
} from '../redux/actions/controlActions';
import { Navigation, ActionButton } from '../components/NavigationButtons';

export default function RiskActionPlan({ 
  modelId, 
  assessmentUuid, 
  riskArea, 
  source, 
  onNext,
  onBack 
}) {
  const dispatch = useDispatch();
  
  // Get the current risk for this source
  const areaRisks = useSelector(s =>
    s.risks.byAssessment[assessmentUuid]?.modelRisks?.[riskArea]?.areaRisks?.[source.name] || []
  );
  const savedRisk = areaRisks.find(risk => risk.status === 'Saved');

  // Get model data from Redux for control model type/stage
  const model = useSelector(s => s.risks.byAssessment[assessmentUuid]);
  const controlModelType = model?.modelType || null;
  const controlModelStage = model?.modelStage || null;
  
  // Get generated controls for current risk from state.controls
  const generatedControls = useSelector(s => 
    savedRisk ? (s.controls.controls[savedRisk.riskId] || []) : []
  );
  
  const isLoading = useSelector(s => s.controls.loading);
  const savedControls = useSelector(s => s.controls.savedControls || {});
  const savedControlEvaluations = useSelector(s => s.controls.savedControlEvaluations || {});
  
  // Get saved controls for this specific risk
  const riskSavedControls = savedRisk 
    ? (savedControls[savedRisk.riskId] || []).filter(c => c.modelId === modelId)
    : [];
  
  // Local state
  const [editingAction, setEditingAction] = useState(null);
  const [editText, setEditText] = useState('');
  //const [drawerOpened, setDrawerOpened] = useState(false);
  // const [drawerActions, setDrawerActions] = useState([]);
  const [savingActionId, setSavingActionId] = useState(null);
  const [showSavedCard, setShowSavedCard] = useState(false);
  const [justSavedId, setJustSavedId] = useState(null);
  // Track newly added generated actions (controlId -> timestamp)
  const [newlyAdded, setNewlyAdded] = useState({});
  // Keep previous generated ids to detect new ones
  const prevIdsRef = useRef(new Set());
  const prevRiskIdRef = useRef(savedRisk?.riskId ?? null);

  // Local view of generated actions that appends (deduped by controlId)
  const [generatedView, setGeneratedView] = useState([]);

  // Append/merge on generation, and hard reset on risk change (satisfies exhaustive-deps)
  useEffect(() => {
    const currentRiskId = savedRisk?.riskId ?? null;
    const riskChanged = prevRiskIdRef.current !== currentRiskId;

    if (riskChanged) {
      // Hard reset when switching risks
      setGeneratedView(generatedControls || []);
      setNewlyAdded({});
      prevIdsRef.current = new Set((generatedControls || []).map((i) => i.controlId));
      prevRiskIdRef.current = currentRiskId;
      return;
    }

    // Append into local view and dedupe by controlId
    setGeneratedView((prev) => {
      const map = new Map();
      [...prev, ...(generatedControls || [])].forEach((item) => {
        if (item && item.controlId != null) map.set(item.controlId, item);
      });
      return Array.from(map.values());
    });

    // Detect new ids compared to previous snapshot
    const nextIds = new Set((generatedControls || []).map((i) => i.controlId));
    const newly = [];
    nextIds.forEach((id) => {
      if (!prevIdsRef.current.has(id)) newly.push(id);
    });

    // Update snapshot before scheduling timers to avoid double-marking
    prevIdsRef.current = nextIds;

    if (newly.length) {
      const now = Date.now();
      setNewlyAdded((prev) => {
        const copy = { ...prev };
        newly.forEach((id) => {
          copy[id] = now;
        });
        return copy;
      });

      // Auto-clear NEW badges after 90 seconds
      const timers = newly.map((id) =>
        setTimeout(() => {
          setNewlyAdded((prev) => {
            const copy = { ...prev };
            delete copy[id];
            return copy;
          });
        }, 90000)
      );

      // Cleanup timers on subsequent updates
      return () => {
        timers.forEach(clearTimeout);
      };
    }
  }, [generatedControls, savedRisk?.riskId]);

  // Show saved card when there are saved controls
  useEffect(() => {
    if (riskSavedControls.length > 0 && !showSavedCard) {
      setShowSavedCard(true);
    }
  }, [riskSavedControls.length, showSavedCard]);

  // Reset when source changes
  useEffect(() => {
    setEditingAction(null);
  }, [source.name, riskArea]);

  if (!savedRisk) {
    return (
      <Container fluid px="xl" py="xl">
        <Alert 
          icon={<IconAlertCircle size={16} />} 
          title="No Saved Risk" 
          color="yellow"
        >
          Please save a risk before generating an action plan.
        </Alert>
      </Container>
    );
  }

  const savedActionIds = new Set(
    Object.values(savedControls).flat()
      .filter(c => c.modelId === modelId)
      .map(c => c.controlId)
  );

  // Filter out saved actions from generated actions
  const generatedActions = generatedView.filter((action) =>
    !savedActionIds.has(action.controlId)
  );

  const hasAnyActions = generatedActions.length > 0 || riskSavedControls.length > 0;

  const handleGenerateActions = async () => {
    await dispatch(fetchControls({
      sectionName: riskArea,
      narratives: [],
      riskSourceData: {
        name: source.name,
        value: source.value || [],
        causes: source.causes || {}
      },
      areaRisks: [savedRisk],
      controlModelType,
      controlModelStage
    }));
  };

  const handleGenerateMore = async () => {
    await dispatch(fetchControls({
      sectionName: riskArea,
      narratives: [],
      riskSourceData: {
        name: source.name,
        value: source.value || [],
        causes: source.causes || {}
      },
      areaRisks: [savedRisk],
      controlModelType,
      controlModelStage
    }));
  };

  const handleSaveAction = async (action) => {
    setSavingActionId(action.controlId);
    
    try {
      await dispatch(saveControl({
        risk: savedRisk,
        control: action,
        modelId,
        assessmentUuid,
        controlModelType,
        controlModelStage,
      }));
      
      // Trigger animation
      setTimeout(() => {
        setSavingActionId(null);
        setJustSavedId(action.controlId);
        setTimeout(() => setJustSavedId(null), 1000);
      }, 300);
      
      if (!showSavedCard) {
        setShowSavedCard(true);
      }
      
      notifications.show({
        title: 'Action Saved',
        message: 'Action has been saved successfully',
        color: 'green',
        icon: <IconCheck size={16} />
      });
    } catch (error) {
      setSavingActionId(null);
      notifications.show({
        title: 'Save Failed',
        message: error.message || 'Failed to save action',
        color: 'red'
      });
    }
  };

  const handleDiscardAction = (action) => {
    dispatch(discardControl(action, savedRisk.riskId));
    // Optimistically remove from local generated view as reducer won't touch our local cache
    setGeneratedView(prev => prev.filter(a => a.controlId !== action.controlId));
    setNewlyAdded(prev => {
      const copy = { ...prev };
      delete copy[action.controlId];
      return copy;
    });
    notifications.show({
      title: 'Action Discarded',
      message: 'Action has been discarded',
      color: 'orange',
      icon: <IconTrash size={16} />
    });
  };

  const handleEditAction = (action) => {
    setEditingAction(action.controlId);
    setEditText(action.name);
  };

  const handleSaveEdit = () => {
    if (editText.trim() && editingAction) {
      dispatch(editGeneratedActionPlanRow(editingAction, editText.trim()));
      notifications.show({
        title: 'Action Updated',
        message: 'Action name has been updated successfully',
        color: 'green',
        icon: <IconCheck size={16} />
      });
    }
    setEditingAction(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingAction(null);
    setEditText('');
  };

  // // Check if action has evaluation
  // const hasEvaluation = (actionId) => {
  //   const evalData = savedControlEvaluations[savedRisk.riskId]?.[actionId]?.[0];
  //   return evalData && (evalData.control_recurrence || evalData.executed_next || evalData.self_evaluation);
  // };

  // Action item component for generated actions
  const GeneratedActionItem = ({ action }) => {
    const isSaving = savingActionId === action.controlId;
    const isEditing = editingAction === action.controlId;
    
    return (
      <Paper
        withBorder
        p="md"
        radius="md"
        sx={(theme) => ({
          borderColor: theme.colors.gray[2],
          transition: 'all 0.3s ease',
          ...(isSaving && {
            animation: 'saveAction 0.3s ease-out',
            '@keyframes saveAction': {
              '0%': { 
                transform: 'scale(1) translateX(0)',
                opacity: 1,
              },
              '50%': { 
                transform: 'scale(0.95) translateX(10px)',
                opacity: 0.8,
              },
              '100%': { 
                transform: 'scale(0.9) translateX(20px)',
                opacity: 0,
              }
            }
          }),
          '&:hover': {
            borderColor: theme.colors.gray[3],
            boxShadow: theme.shadows.sm,
          }
        })}
      >
        <Stack gap="sm">
          {isEditing ? (
            <Group gap="xs" align="center">
              <input
                style={{ 
                  flex: 1, 
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                autoFocus
              />
              <ActionIcon 
                size="sm" 
                color="green" 
                onClick={handleSaveEdit}
              >
                <IconCheck size={14} />
              </ActionIcon>
              <ActionIcon 
                size="sm" 
                color="red" 
                onClick={handleCancelEdit}
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
          ) : (
            <>
              <Group justify="space-between" align="center">
                <Text size="sm" style={{ lineHeight: 1.5 }}>
                  {action.name}
                </Text>
                {newlyAdded[action.controlId] && (
                  <Badge variant="filled" color="teal" size="xs" radius="sm">
                    New
                  </Badge>
                )}
              </Group>
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  color="indigo"
                  leftSection={<IconDeviceFloppy size={14} />}
                  onClick={() => handleSaveAction(action)}
                  disabled={isSaving}
                >
                  Save
                </Button>
                <ActionIcon
                  size="sm"
                  variant="light"
                  onClick={() => handleEditAction(action)}
                  disabled={isSaving}
                >
                  <IconEdit size={14} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="light"
                  color="red"
                  onClick={() => handleDiscardAction(action)}
                  disabled={isSaving}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            </>
          )}
        </Stack>
      </Paper>
    );
  };

  // Saved action item component
  const SavedActionItem = ({ control, isNew }) => {
    const isEvaluated = savedControlEvaluations[savedRisk.riskId]?.[control.controlId]?.[0];
    
    return (
      <Paper
        withBorder
        p="md"
        radius="md"
        sx={(theme) => ({
          borderColor: theme.colors.gray[2],
          backgroundColor: theme.colors.gray[0],
          ...(isNew && {
            animation: 'appearAction 0.5s ease-out',
            '@keyframes appearAction': {
              '0%': { 
                transform: 'translateY(-10px)',
                opacity: 0,
              },
              '100%': { 
                transform: 'translateY(0)',
                opacity: 1,
              }
            }
          }),
        })}
      >
        <Stack gap="xs">
          <Group gap="xs">
            <Group gap="xs">
              <IconCheck size={16} color="#40c057" />
              <Text size="sm" fw={500}>
                {control.name}
              </Text>
              {isNew && (
                <Badge variant="filled" color="teal" size="xs" radius="sm">
                  New
                </Badge>
              )}
            </Group>
            <Group gap="xs">
              <Badge 
                variant="light" 
                color={isEvaluated ? 'green' : 'gray'}
                size="sm"
              >
                {isEvaluated ? 'Evaluated' : 'Not Evaluated'}
              </Badge>
              <ActionIcon
                size="sm"
                variant="light"
                color="red"
                onClick={() => {
                  dispatch(discardSavedControl(control, savedRisk.riskId));
                  notifications.show({
                    title: 'Saved Action Discarded',
                    message: 'The saved action has been removed',
                    color: 'orange',
                    icon: <IconTrash size={16} />
                  });
                }}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          </Group>
        </Stack>
      </Paper>
    );
  };

  return (
    <Container fluid px="xl" py="xl">
      {/* Header */}
      <Stack gap="md" mb="xl">
        <div>
          <Title order={3} c="dark.7">
            Mitigation Plan
          </Title>
          <Text size="md" c="dimmed">
            Develop actions to mitigate identified risks
          </Text>
        </div>
        
        <Group gap="md">
          <Text size="md" c="dimmed">
            Addressing risk:
          </Text>
          <Badge 
            variant="filled"
            size="lg"
            radius="sm"
            sx={(theme) => ({
              backgroundColor: theme.colors.indigo[5],
              color: theme.white,
            })}
          >
            {savedRisk.refinedRisk || savedRisk.riskName}
          </Badge>
        </Group>
      </Stack>

      <Grid gutter="lg">
        {/* Left Column - Generation Area */}
        <Grid.Col span={showSavedCard ? 6 : 12}>
          <Card withBorder radius="md" p="lg" h="100%">
            <Stack gap="md">
              <Title order={5} c="dark.7">
                Generate Actions
              </Title>

              {/* Generation Buttons */}
              {hasAnyActions ? (
                <Group gap="sm">
                  <ActionButton
                    onClick={handleGenerateMore}
                    disabled={isLoading}
                    leftSection={<AIGeneratingIcon isGenerating={isLoading} size={16} color="white" />}
                    style={{ flex: 1 }}
                  >
                    {isLoading ? 'Generating...' : 'Generate More'}
                  </ActionButton>
                </Group>
              ) : null}

              {/* Generated Actions List */}
              {generatedActions.length > 0 && (
                <>
                  <Divider />
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" fw={500} c="dimmed">
                        Generated Actions
                      </Text>
                      <Badge variant="light" color="indigo" size="sm">
                        {generatedActions.length} new
                      </Badge>
                    </Group>
                    
                    <Stack gap="sm">
                      {generatedActions.map(action => (
                        <GeneratedActionItem key={action.controlId} action={action} />
                      ))}
                    </Stack>
                  </Stack>
                </>
              )}

              {/* Empty State */}
              {generatedActions.length === 0 && !isLoading && !hasAnyActions && (
                <Paper
                  p="xl"
                  radius="md"
                  sx={(theme) => ({
                    backgroundColor: theme.colors.gray[0],
                    textAlign: 'left',
                  })}
                >
                  <Stack align="center" gap="md">
                    <Group gap="md" align="center">
                      <IconSparkles size={48} color="#868e96" />
                      <div>
                        <Text fw={500} mb="xs">No Actions Generated Yet</Text>
                        <Text size="sm" c="dimmed">
                          Click "Generate Actions" to create AI-powered mitigation actions.
                        </Text>
                      </div>
                    </Group>
                    <ActionButton
                      onClick={handleGenerateActions}
                      disabled={isLoading}
                      leftSection={<AIGeneratingIcon isGenerating={isLoading} size={16} color="white" />}
                    >
                      {isLoading ? 'Generating...' : 'Generate Actions'}
                    </ActionButton>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Card>
        </Grid.Col>

        {/* Right Column - Saved Actions (slides in) */}
        <Transition 
          mounted={showSavedCard} 
          transition="fade-left" 
          duration={400} 
          timingFunction="ease"
        >
          {(styles) => (
            <Grid.Col span={6} style={styles}>
              <Card 
                withBorder 
                radius="md" 
                p="lg" 
                h="100%"
                sx={(theme) => ({
                  backgroundColor: theme.colors.gray[0],
                  borderColor: theme.colors.gray[2],
                })}
              >
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Title order={5} c="dark.7">
                      Saved Actions
                    </Title>
                    <Badge variant="filled" color="green" size="lg">
                      {riskSavedControls.length} saved
                    </Badge>
                  </Group>

                  <Divider />

                  {/* Saved Actions List */}
                  <Stack gap="sm">
                    {riskSavedControls.map(control => (
                      <SavedActionItem 
                        key={control.controlId} 
                        control={control}
                        isNew={justSavedId === control.controlId}
                      />
                    ))}
                  </Stack>

                  {riskSavedControls.length === 0 && (
                    <Text size="sm" c="dimmed" ta="left" py="xl">
                      Saved actions will appear here
                    </Text>
                  )}
                </Stack>
              </Card>
            </Grid.Col>
          )}
        </Transition>
      </Grid>

      {/* Navigation */}
      <Navigation
        onBack={onBack}
        onNext={onNext}
        nextLabel="Continue to Control Evaluation"
        nextDisabled={riskSavedControls.length === 0}
        showBack={!!onBack}
      />

    </Container>
  );
}
